import * as React from 'react';
import { Link } from 'react-router';
import { AccountsError, requestPasswordReset } from '../accounts.ts';
import { AUTH_LINK, AUTH_SUBMIT, AuthField, AuthNotice, AuthShell } from './AuthShell.tsx';

export const FORGOT_DONE_TEXT =
  'If an account exists for that address, we sent it a link to set a new password. It works once, for 30 minutes.';

/**
 * "Forgot password?" (ADR 0014 §10): asks the accounts service to mail a one-time reset link. The page says
 * the same thing whether or not the address has an account.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = React.useState(
    () => new URLSearchParams(window.location.search).get('email') ?? '',
  );
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await requestPasswordReset(email.trim());
      setDone(true);
    } catch (err) {
      setError(err instanceof AccountsError ? err.message : 'The request failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Forgot password"
      subtitle="We will email you a link to set a new one"
      testId="forgot-page"
    >
      {done ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="ok" testId="forgot-done">
            {FORGOT_DONE_TEXT}
          </AuthNotice>
          <Link to="/login" className={AUTH_LINK}>
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {error && (
            <AuthNotice kind="error" testId="forgot-error">
              {error}
            </AuthNotice>
          )}
          <AuthField
            id="forgot-email"
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            testId="forgot-email"
          />
          <button type="submit" disabled={busy} data-testid="forgot-submit" className={AUTH_SUBMIT}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
          <Link to="/login" className={AUTH_LINK}>
            Back to sign in
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
